import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Modal } from '../Modal';
import { Button } from './ui';
import { useTranslation } from '../../lib/i18n';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useItemSeenBi } from '../../lib/use-item-seen-bi';
import { useBlackFridayRedeem } from '../../lib/use-black-friday-redeem';
import { BLACK_FRIDAY_RESET_EVENT } from '../../lib/black-friday-storage';
import { BLACK_FRIDAY_MODAL_SOURCE } from '../../lib/black-friday';
import { BlackFridayFallingCircles } from './BlackFridayFallingCircles';
import styles from './BlackFridayModal.module.css';

const PAGE = 'black_friday_modal';

/**
 * The letters cluster at the foot of the card — "BLACK FRIDAY" scattered over the frame's own two
 * rows, with unlettered and ringed circles between them (Figma `4217:25724`).
 *
 * The letters are the campaign's name and are NOT copy: they stay B·L·A·C·K · F·R·I·D·A·Y in every
 * locale, the way a logotype does, so they carry no catalog key — and `aria-hidden`, because the
 * headline above has already said it. Each circle's centre, fill and tilt live in the stylesheet;
 * this array only says which circle carries which letter, so the two can never drift apart.
 *
 * The animated cluster (`BlackFridayFallingCircles`) is what readers see. This box stays as the
 * card's foot either way — it holds the height the animation lands in — and draws its circles only
 * when the animation could not load (`circles`).
 */
const CLUSTER: { at: string; fill: string; char?: string }[] = [
  { at: styles.b, fill: styles.cyan, char: 'B' },
  { at: styles.l, fill: styles.orange, char: 'L' },
  { at: styles.g2, fill: styles.ring2, char: undefined },
  { at: styles.a2, fill: styles.lime, char: 'A' },
  { at: styles.c, fill: styles.pink, char: 'C' },
  { at: styles.k, fill: styles.orange, char: 'K' },
  { at: styles.i, fill: styles.pink, char: 'I' },
  { at: styles.d, fill: styles.cyan, char: 'D' },
  { at: styles.p2, fill: styles.blue, char: undefined },
  { at: styles.a1, fill: styles.orange, char: 'A' },
  { at: styles.g1, fill: styles.ring1, char: undefined },
  { at: styles.p1, fill: styles.blue, char: undefined },
  { at: styles.f, fill: styles.cyan, char: 'F' },
  { at: styles.r, fill: styles.lime, char: 'R' },
  { at: styles.p4, fill: styles.blue, char: undefined },
  { at: styles.g3, fill: styles.ring3, char: undefined },
  { at: styles.y, fill: styles.pink, char: 'Y' },
  { at: styles.p3, fill: styles.lime, char: undefined }
];

function Letters({ circles }: { circles: boolean }) {
  return (
    <div className={styles.letters} aria-hidden>
      {circles &&
        CLUSTER.map(({ at, fill, char }, index) => (
          <span key={index} className={`${styles.circle} ${fill} ${at}`}>
            {char}
          </span>
        ))}
    </div>
  );
}

/**
 * Splits the headline around `{campaign}` so "Black Friday" can carry the display serif the frame draws,
 * without cutting the sentence into two catalog keys.
 *
 * Two keys would pin the English word order into all 37 locales — a translator could not move "Black
 * Friday" to where their language wants it. One key with a placeholder leaves the order free; it is only
 * split here because `t()` interpolates STRINGS and this one needs a node.
 */
function headlineParts(template: string, campaign: string): ReactNode {
  const [before, after = ''] = template.split('{campaign}');
  return (
    <>
      {before}
      <span className={styles.headlineAccent}>{campaign}</span>
      {after}
    </>
  );
}

/**
 * The one-time Black Friday offer modal (Figma 4328:48388). **Web only** — a deliberate divergence from
 * the phones, which show the band alone, because the campaign's modal is a desktop merchandising surface
 * and the design draws no phone frame for it. Written down in `native-apps/CLAUDE.md` too.
 *
 * Mounted at the frame beside `PhoneVerifyModal` and renders nothing until it applies, so the common case
 * costs nothing. Whether it applies is `useBlackFriday`, which the frame already calls — the gate and the
 * "mark it seen" write are passed in rather than read again here, so the band and the modal cannot end up
 * holding two different copies of what this browser remembers.
 *
 * **It is marked seen when it OPENS, not when it is actioned.** A reader who closed it has seen it; asking
 * again on the next load is the thing "once per user" rules out. `open` is this component's own state for
 * exactly that reason — `gate` goes false the moment it is marked, and a card that unmounted itself
 * mid-paint would never be readable.
 *
 * The CTA spends the same code the band's does, through the same one redemption path.
 *
 * **Group B of the A/B test (`running`) draws the same card with different words**: the code was already
 * redeemed for the reader on login, so the card says the 48 hours are running and its CTA redeems
 * nothing — it reports the press and closes. Which copy the card wears is latched when it OPENS, because
 * `running` goes false the moment the card is marked seen.
 */
export function BlackFridayModal({
  gate,
  running = false,
  narrow,
  onSeen,
}: {
  gate: boolean;
  /** Group B: the hours are already running, so the card says so and its CTA only closes it. */
  running?: boolean;
  /** Closes an OPEN card when the window is dragged under the breakpoint: `gate` has gone false by then
      (it is false for every reason the frame suppresses this card), so only this can still close it, and
      the shell's `fullScreenOnSmall` would otherwise turn it into the phone takeover this campaign
      deliberately does not have. The latch stays set, so it does not reopen on the way back out. */
  narrow: boolean;
  onSeen: () => void;
}) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  // The impression (198:6) — the denominator the modal's two actions are measured against. `modalAction`
  // is a CLICK event (it maps to `clickCta`), so neither `start` nor `close` can stand in for "it was
  // shown": a reader who navigated away without touching it would be missing from both halves. The
  // storage latch beside it (`onSeen`) answers "has this browser had its one shot", which is a different
  // question and is not BI.
  const redeem = useBlackFridayRedeem(BLACK_FRIDAY_MODAL_SOURCE);
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const [runningCopy, setRunningCopy] = useState(false);
  const seenRef = useItemSeenBi('black-friday-modal', {
    additionalFields: { modal_variant: runningCopy ? 'running' : 'offer' },
  });
  const [animationFailed, setAnimationFailed] = useState(false);
  const onAnimationFail = useCallback(() => setAnimationFailed(true), []);

  useEffect(() => {
    if (!gate || shown) return;
    setShown(true);
    setRunningCopy(running);
    setOpen(true);
    onSeen();
  }, [gate, shown, running, onSeen]);

  useEffect(() => {
    if (narrow) setOpen(false);
  }, [narrow]);

  // The card stays up until the service answers, so the wait and any refusal are seen here. On success
  // the band takes over — the redemption refreshes the credits overview, which comes back
  // carrying `freeUsageUntil`, and the band resolves to the countdown off that one source of truth.
  useEffect(() => {
    if (redeem.redeemed) setOpen(false);
  }, [redeem.redeemed]);

  useEffect(() => {
    const rearm = () => setShown(false);
    window.addEventListener(BLACK_FRIDAY_RESET_EVENT, rearm);
    return () => window.removeEventListener(BLACK_FRIDAY_RESET_EVENT, rearm);
  }, []);

  if (!open) return null;

  const modalName = runningCopy ? 'black_friday_running' : 'black_friday';
  // Spelled out key by key (no template) so the catalog guards can see every one of them.
  const copy = runningCopy
    ? {
        eyebrow: t('blackFriday.modalRunning.eyebrow'),
        title: t('blackFriday.modalRunning.title'),
        body: t('blackFriday.modalRunning.body'),
        cta: t('blackFriday.modalRunning.cta'),
      }
    : {
        eyebrow: t('blackFriday.modal.eyebrow'),
        title: t('blackFriday.modal.title'),
        body: t('blackFriday.modal.body'),
        cta: t('blackFriday.modal.cta'),
      };

  const close = (action: string) => {
    logModalActionBi({ page_name: PAGE, modal_name: modalName, action });
    setOpen(false);
  };

  const onStart = () => {
    if (runningCopy) {
      close('get_ready');
      return;
    }
    logModalActionBi({ page_name: PAGE, modal_name: modalName, action: 'start' });
    redeem.redeem();
  };

  const messageValues = redeem.message?.values
    ? Object.fromEntries(Object.entries(redeem.message.values).map(([k, v]) => [k, v.toLocaleString()]))
    : undefined;

  return (
    <Modal
      open
      onClose={() => close('close')}
      size="small"
      width={673}
      maxHeightPx={650}
      scrollBody
      cardClassName={styles.card}
      contentClassName={styles.slot}
      cardTheme="dark"
      labelledBy="black-friday-modal-title"
    >
      <div ref={seenRef} className={styles.body}>
        {!animationFailed && <BlackFridayFallingCircles onFail={onAnimationFail} />}
        <div className={styles.headlineGroup}>
          {/* The braces are the brand's serif-label device, not copy, so they stay out of the catalog. */}
          <span className={styles.eyebrow}>{`{${copy.eyebrow}}`}</span>
          <h2 id="black-friday-modal-title" className={styles.headline}>
            {headlineParts(copy.title, t('blackFriday.modal.campaignName'))}
          </h2>
        </div>
        <p className={styles.copy}>{copy.body}</p>
        <span className={styles.cta}>
          <Button
            size="large"
            className={styles.ctaButton}
            loading={!runningCopy && redeem.busy}
            disabled={!runningCopy && !redeem.canPress}
            onClick={onStart}
          >
            {copy.cta}
          </Button>
        </span>
        {!runningCopy && redeem.message && (
          <span className={`${styles.message} ${redeem.message.tone === 'error' ? styles.messageError : ''}`} role="status">
            {t(redeem.message.key, messageValues)}
          </span>
        )}
        <Letters circles={animationFailed} />
      </div>
    </Modal>
  );
}
